import { GridSortModel } from "@mui/x-data-grid-pro";
import { invalidateEntireGamesDetailsQuery } from "entities/game/queries/useGameDetailsQuery";
import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";
import {
  useHits,
  useConfigure,
  useSearchBox,
  useInstantSearch,
  useSortBy,
} from "react-instantsearch";
import { Game, GameWithTagsCount } from "../types";

export const ALGOLIA_INDEX_NAME =
  process.env.REACT_APP_ALGOLIA_GAMES_INDEX_NAME || "";

// Current hook serves to display table data
export const useGamesData = ({
  filterTags = [],
  isActive = [],
  size = 25,
}: {
  isActive: string[];
  filterTags?: string[];
  size?: number;
}) => {
  const initialPaginationModal = {
    pageSize: size,
    page: 0,
  };

  const [paginationModel, setPaginationModel] = useState(
    initialPaginationModal,
  );
  const { query, refine: refineSearch, clear } = useSearchBox();
  const { hits, results } = useHits<Game>();
  const { status, refresh } = useInstantSearch();
  const { refine: sorting } = useSortBy({
    items: [],
  });

  useEffect(() => {
    if (hits.length) {
      refresh();
      invalidateEntireGamesDetailsQuery();
    }

    return () => {
      clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onResetPage = () =>
    setPaginationModel((prev) => ({ ...prev, page: 0 }));

  const isActiveFilter =
    isActive.length > 0
      ? `(${isActive.map((id) => `isActive:${id}`).join(" OR ")})`
      : "";

  const filterTagsString = filterTags.filter(Boolean).some((tag) => !!tag)
    ? filterTags.filter(Boolean).join(" AND ")
    : "";

  const filters = [isActiveFilter, filterTagsString]
    .filter(Boolean)
    .join(" AND ");

  useConfigure({
    query,
    hitsPerPage: paginationModel.pageSize,
    page: paginationModel.page,
    filters,
  });

  const data: GameWithTagsCount[] = useMemo(
    () =>
      hits.map((hit) => ({
        ...hit,
        id: hit.id,
        tagsCount: hit.tags.length,
      })),
    [hits],
  );

  const onSort = (fields: GridSortModel) => {
    onResetPage();

    if (fields[0]) {
      const { field, sort } = fields[0];
      sorting(`${ALGOLIA_INDEX_NAME}_${field}_${sort}`);
    } else {
      sorting(ALGOLIA_INDEX_NAME);
    }
  };

  const onSearch = debounce((value) => {
    refineSearch(value);
    onResetPage();
  }, 500);

  const { nbHits = 0 } = results || {};
  const isLoading = status === "loading";

  return {
    searchData: {
      value: query,
      onChange: onSearch,
    },
    tableData: {
      data,
      rowCount: nbHits,
      onSortModelChange: onSort,
      paginationModel: paginationModel,
      onPaginationModelChange: setPaginationModel,
    },
    isLoading,
    onResetPage,
  };
};
